// =============================================================================
// DATAHEALTH.JS — The "To-do" list: records that need someone to look at them.
// =============================================================================
// Built from the 24 Oct 2026 action plan (AgencyBase-plan-actions-2026-10-24).
// Each check reproduces one of its counts on live data, so the list shrinks as
// people fix things instead of going stale like a PDF.
//
// These rules FLAG; they never change a record. The plan is explicit that a
// status must not move to Posted or Paid/Done just because a link exists: the
// person who owns the deal confirms what really happened, then edits it.
//
// Deals passed in should be current ones (not is_historical), like the rest of
// the app's operational views.
// =============================================================================

import { overdueInvoices } from './overdue'
import { isPaidStatus } from './constants'

const MS_PER_DAY = 86400000

// --- links ---------------------------------------------------------------------

const URL_RE = /https?:\/\/[^\s"'<>]+/gi

/** Every http(s) URL in a field, in order. Trailing punctuation is trimmed. */
export function extractUrls(value) {
  if (!value) return []
  return (String(value).match(URL_RE) || []).map(u => u.replace(/[.,;:)\]]+$/, ''))
}

/** True for a value that is exactly one http(s) URL (surrounding spaces ok). */
export function isSingleUrl(value) {
  const v = String(value || '').trim()
  return /^https?:\/\/\S+$/i.test(v) && extractUrls(v).length === 1
}

export const isQuickBooksUrl = (url) => /(^|\.)intuit\.com|quickbooks/i.test(String(url || ''))

/**
 * What is wrong with a link field, or null. Content may hold several URLs
 * (a TikTok and its Instagram repost) separated by spaces or new lines; the
 * other link fields hold exactly one.
 */
export function linkProblem(value, field) {
  const v = String(value || '').trim()
  if (!v) return null
  const urls = extractUrls(v)
  if (!urls.length) return 'text'                       // "No Contract", a name, a note
  const leftover = urls.reduce((s, u) => s.replace(u, ' '), v).replace(/[\s.,;]+/g, '')
  if (leftover) return 'annotated'                      // quotes or labels around the URL
  if (field === 'content_url' && urls.some(isQuickBooksUrl)) return 'quickbooks'
  if (field !== 'content_url' && urls.length > 1) return 'multiple'
  return null
}

export const LINK_PROBLEM_TEXT = {
  text: 'is text, not a link',
  annotated: 'has text or quotes around the link',
  quickbooks: 'holds a QuickBooks invoice link instead of the content',
  multiple: 'holds more than one link',
}

/** Message for the deal form, or null when the value is fine. */
export function linkFieldError(value, field, label) {
  const p = linkProblem(value, field)
  if (!p) return null
  const tips = {
    text: 'Paste a link (https://…). If there is no document, write that in Notes instead.',
    annotated: 'Keep only the link; put labels or comments in Notes.',
    quickbooks: 'Put the QuickBooks link in Invoice Link and the post itself here.',
    multiple: 'Keep one link here and put the others in Notes.',
  }
  return `${label} ${LINK_PROBLEM_TEXT[p]}. ${tips[p]}`
}

// --- emails --------------------------------------------------------------------

const EMAIL_RE = /^[^\s@,;<>]+@[^\s@,;<>]+\.[a-z]{2,}$/i

export function emailProblem(value) {
  const v = String(value || '').trim()
  if (!v) return null
  if (EMAIL_RE.test(v)) return null
  const found = v.match(/[^\s@,;<>]+@[^\s@,;<>]+\.[a-z]{2,}/gi) || []
  if (found.length > 1) return 'several addresses in one field'
  if (found.length === 1) return 'extra text around the address'
  return 'not an email address'
}

// --- names ---------------------------------------------------------------------

/** Spelling-insensitive key: "Alan Cavé" = "Alan Cave", "SheaMoisture" = "Shea Moisture". */
export function nameKey(name) {
  return String(name || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\b(llc|inc|ltd|ltee|ltée|corp)\.?$/g, '')
    .replace(/[^a-z0-9]/g, '')
}

/** Groups of names that differ only in spelling: [['Alan Cave', 'Alan Cavé'], …]. */
export function nameVariants(names) {
  const groups = new Map()
  for (const raw of names || []) {
    const n = String(raw || '').trim()
    if (!n) continue
    const k = nameKey(n)
    if (!k) continue
    if (!groups.has(k)) groups.set(k, new Set())
    groups.get(k).add(n)
  }
  return [...groups.values()].filter(s => s.size > 1).map(s => [...s].sort())
}

// --- the checks ----------------------------------------------------------------

const has = (v) => v !== null && v !== undefined && String(v).trim() !== ''
const dealLabel = (d) => `${d.creator || '?'} × ${d.brand || d.client || '?'}`
const day = (d) => (d ? String(d).slice(0, 10) : '')
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-CA')}`
const OPEN_STAGES = ['Concept', 'In Production', 'Approved']
const BEFORE_POSTING = ['Concept', 'In Production', 'Waiting For Approval']
const isClosedLead = (s) => s === 'Won' || s === 'Lost'

function dealItem(d, detail) {
  return { kind: 'deal', id: d.id, label: dealLabel(d), owner: d.agent || 'Unassigned', detail, status: d.status }
}

/**
 * Everything the action plan asks people to look at, as sections of checks.
 * `today` is 'YYYY-MM-DD'.
 */
export function buildDataHealth({ deals = [], leads = [], creators = [], team = [], today, now = new Date() }) {
  const todayStr = today || now.toISOString().slice(0, 10)
  const hasInvoice = (d) => has(d.invoice_url) || has(d.invoice_path)
  const hasContract = (d) => has(d.contract_url) || has(d.contract_path)

  // 01 — payments
  const overdue = overdueInvoices(deals, now).map(d => dealItem(d, `${d.daysOverdue} days past due · ${money(d.deal_amount)}`))
  const invoiceSentNoLink = deals.filter(d => d.status === 'Invoice Sent' && !hasInvoice(d)).map(d => dealItem(d, 'Invoice Sent, no invoice link'))
  const postedNoInvoice = deals.filter(d => d.status === 'Posted' && !hasInvoice(d)).map(d => dealItem(d, 'Posted, no invoice link'))
  const paidNoInvoice = deals.filter(d => isPaidStatus(d.status) && !hasInvoice(d)).map(d => dealItem(d, 'Paid, no invoice on file'))
  const partial = deals.filter(d => d.status === 'Partially Paid').map(d => dealItem(d,
    has(d.amount_collected) ? `${money(d.amount_collected)} of ${money(d.deal_amount)} received` : `amount received not recorded · ${money(d.deal_amount)}`))

  // 02 — campaigns
  const pastDate = deals.filter(d => OPEN_STAGES.includes(d.status) && has(d.date_action) && day(d.date_action) < todayStr)
    .map(d => dealItem(d, `${d.status} · date was ${day(d.date_action)}`))
  const linkButEarly = deals.filter(d => BEFORE_POSTING.includes(d.status) && has(d.content_url))
    .map(d => dealItem(d, `${d.status}, but a content link is saved`))

  // 03 — files
  const missingDate = deals.filter(d => OPEN_STAGES.includes(d.status) && !has(d.date_action)).map(d => dealItem(d, `${d.status}, no due/post date`))
  const missingDeliverables = deals.filter(d => !has(d.deliverables)).map(d => dealItem(d, 'No deliverables entered'))
  const missingContract = deals.filter(d => !hasContract(d)).map(d => dealItem(d, 'No contract link'))
  const postedNoContent = deals.filter(d => d.status === 'Posted' && !has(d.content_url)).map(d => dealItem(d, 'Posted, no content link'))

  // 04 — links
  const badLinks = []
  for (const d of deals) {
    for (const [field, label] of [['contract_url', 'Contract'], ['invoice_url', 'Invoice'], ['content_url', 'Content'], ['beacons_url', 'Beacons'], ['media_kit_url', 'Media kit']]) {
      const p = linkProblem(d[field], field)
      if (p) badLinks.push(dealItem(d, `${label} link ${LINK_PROBLEM_TEXT[p]}`))
    }
  }

  // 05 — CRM
  const overdueFollowUps = leads.filter(l => !isClosedLead(l.status) && has(l.next_follow_up) && day(l.next_follow_up) < todayStr)
    .map(l => ({
      kind: 'lead', id: l.id, label: l.brand || l.client || l.contact_name || 'Lead', owner: l.owner_name || 'Unassigned', status: l.status,
      detail: `${l.status || 'Open'} · follow-up was ${day(l.next_follow_up)} (${Math.floor((new Date(todayStr) - new Date(day(l.next_follow_up))) / MS_PER_DAY)} days)`,
    }))
  const brands = new Map()
  for (const d of deals) {
    if (!has(d.brand)) continue
    const b = brands.get(d.brand) || { contact: false, agents: new Set() }
    if (has(d.contact_person) || has(d.email)) b.contact = true
    if (d.agent) b.agents.add(d.agent)
    brands.set(d.brand, b)
  }
  const brandsNoContact = [...brands.entries()].filter(([, b]) => !b.contact).map(([name, b]) => ({
    kind: 'brand', id: name, label: name, owner: [...b.agents][0] || 'Unassigned', detail: 'No contact on any deal',
  }))
  const badEmails = [
    ...deals.filter(d => emailProblem(d.email)).map(d => dealItem(d, `Email: ${emailProblem(d.email)}`)),
    ...leads.filter(l => emailProblem(l.contact_email)).map(l => ({
      kind: 'lead', id: l.id, label: l.brand || l.contact_name || 'Lead', owner: l.owner_name || 'Unassigned', status: l.status,
      detail: `Email: ${emailProblem(l.contact_email)}`,
    })),
  ]
  const variants = nameVariants([...deals.map(d => d.brand), ...deals.map(d => d.client), ...leads.map(l => l.brand)])
    .map(g => ({ kind: 'name', id: g.join('|'), label: g.join(' / '), owner: 'Admin', detail: 'Same brand spelled differently' }))

  // 06 — administration
  const roster = new Set(creators.map(c => c.name))
  const rosterKeys = creators.map(c => ({ name: c.name, key: nameKey(c.name) }))
  const offRoster = new Map()
  for (const d of deals) if (has(d.creator) && !roster.has(d.creator)) offRoster.set(d.creator, (offRoster.get(d.creator) || 0) + 1)
  const notOnRoster = [...offRoster.entries()].map(([name, n]) => {
    const k = nameKey(name)
    const similar = rosterKeys.find(r => r.key !== k && k && (r.key.startsWith(k) || k.startsWith(r.key)))
    return { kind: 'creator', id: name, label: name, owner: 'Admin',
      detail: `${n} deal${n > 1 ? 's' : ''} but no creator card${similar ? ` · same person as "${similar.name}"?` : ''}` }
  })
  const noSocials = creators.filter(c => !has(c.tiktok_handle) && !has(c.instagram_handle))
    .map(c => ({ kind: 'creator', id: c.id || c.name, label: c.name, owner: c.default_agent || 'Admin', detail: 'No TikTok or Instagram handle' }))
  const noTarget = (team || []).filter(t => (t.role === 'agent' || t.role === 'admin') && t.agent_name && !(Number(t.monthly_target) > 0))
    .map(t => ({ kind: 'person', id: t.id, label: t.agent_name, owner: 'Admin', detail: 'No monthly target' }))

  const check = (id, title, hint, items) => ({ id, title, hint, items })
  return [
    { id: 'payments', number: '01', title: 'Payments & invoicing', checks: [
      check('overdue', 'Overdue invoices', 'Confirm whether it was paid. Update the status, or send a reminder if the balance is really due.', overdue),
      check('invoice-sent-no-link', '"Invoice Sent" without an invoice link', 'Add the link to the invoice that was sent.', invoiceSentNoLink),
      check('posted-no-invoice', '"Posted" without an invoice link', 'Send the invoice if it is due, or add the link if it was already sent.', postedNoInvoice),
      check('paid-no-invoice', '"Paid/Done" without an invoice', 'Add the invoice so the accounting record is complete.', paidNoInvoice),
      check('partial', 'Partial payments', 'Check the amount received, the balance and the next due date.', partial),
    ] },
    { id: 'campaigns', number: '02', title: 'Campaigns', checks: [
      check('past-date', 'Date has passed, status not updated', 'Confirm delivery, posting, postponement or cancellation before changing the status.', pastDate),
      check('link-but-early', 'Content link saved but status is earlier', 'A link does not prove every deliverable is done. Check stories, reposts and raw files before moving it on.', linkButEarly),
    ] },
    { id: 'files', number: '03', title: 'Deal files', checks: [
      check('missing-date', 'No due or post date', 'Enter the next date in the calendar field (it may already be written in the notes).', missingDate),
      check('posted-no-content', '"Posted" without a content link', 'Add the link to the post.', postedNoContent),
      check('missing-deliverables', 'No deliverables', 'Write what was agreed.', missingDeliverables),
      check('missing-contract', 'No contract link', 'Add the contract, or note in Notes what the agreement is if there is no contract.', missingContract),
    ] },
    { id: 'links', number: '04', title: 'Broken links', checks: [
      check('bad-links', 'Link fields with text, quotes or the wrong link', 'Keep only the address in the field; labels and comments go in Notes. Open the link afterwards to check it.', badLinks),
    ] },
    { id: 'crm', number: '05', title: 'CRM', checks: [
      check('overdue-followups', 'Follow-ups past due', 'Log the last real exchange, confirm the status, then set the next date or close it as Won/Lost.', overdueFollowUps),
      check('brands-no-contact', 'Brands with no contact', 'Add the contact person and email on the deal.', brandsNoContact),
      check('bad-emails', 'Email fields to fix', 'One valid address per field.', badEmails),
      check('name-variants', 'Same brand, different spellings', 'Pick one spelling after checking they are the same company.', variants),
    ] },
    { id: 'admin', number: '06', title: 'Profiles & targets', checks: [
      check('not-on-roster', 'Creators on deals but not on the roster', 'Add a creator card (Settings → Creators), or rename the deals to match an existing card.', notOnRoster),
      check('no-socials', 'Creators with no social handles', 'Add their TikTok or Instagram.', noSocials),
      check('no-target', 'Agents with no monthly target', 'Set one in Settings → Team if they should have a target.', noTarget),
    ] },
  ]
}

/** Items of a section/check list kept to one owner ('' = everyone). */
export function filterByOwner(sections, owner) {
  if (!owner) return sections
  return sections.map(s => ({ ...s, checks: s.checks.map(c => ({ ...c, items: c.items.filter(i => i.owner === owner) })) }))
}

export function countItems(sections) {
  return sections.reduce((n, s) => n + s.checks.reduce((m, c) => m + c.items.length, 0), 0)
}

/** Owners with how many items each, most first. */
export function ownersOf(sections) {
  const m = new Map()
  for (const s of sections) for (const c of s.checks) for (const i of c.items) m.set(i.owner, (m.get(i.owner) || 0) + 1)
  return [...m.entries()].sort((a, b) => b[1] - a[1])
}
